package one.healthium.reminders.domain

import co.touchlab.kermit.Logger
import kotlinx.coroutines.sync.Mutex
import kotlinx.coroutines.sync.withLock
import kotlinx.datetime.DatePeriod
import kotlinx.datetime.TimeZone
import kotlinx.datetime.atStartOfDayIn
import kotlinx.datetime.toLocalDateTime
import one.healthium.base.preferences.AppPreferences
import one.healthium.domain.user.UserInteractor
import one.healthium.domain.user.models.UserProfileModel
import one.healthium.domain.user.models.bindPlaceholderValues
import one.healthium.notifications.manager.Notification
import one.healthium.notifications.scheduler.NotificationScheduler
import one.primehealth.app.UserData
import kotlin.time.Clock
import kotlin.time.Duration
import kotlin.time.Duration.Companion.days
import kotlin.time.Duration.Companion.hours
import kotlin.time.Duration.Companion.minutes
import kotlin.time.Instant

class RemindersInteractor(
    private val remindersRepository: RemindersRepository,
    private val appPreferences: AppPreferences,
    private val notificationScheduler: NotificationScheduler,
    private val userInteractor: UserInteractor,
) : UserData {
    private val mutex = Mutex()

    suspend fun setupReminders() {
        mutex.withLock {
            val duration = 14.days
            val now = Clock.System.now()
            val previousRemindersIds = appPreferences.scheduledRemindersIds

            if (previousRemindersIds.isNotEmpty()) {
                Logger.d("Reminders: Canceling previous reminders, ids: $previousRemindersIds")
                notificationScheduler.cancel(previousRemindersIds)
            }

            val user = userInteractor.getUser().profile
            val isMetric = appPreferences.isMetric

            val startOfSchedulePeriod = now
                .toLocalDateTime(TimeZone.currentSystemDefault())
                .date
                .atStartOfDayIn(TimeZone.currentSystemDefault())
            val endOfSchedulePeriod = startOfSchedulePeriod + duration

            Logger.d("Reminders: Scheduling reminders from $startOfSchedulePeriod to $endOfSchedulePeriod")

            val reminders = remindersRepository.getReminders()
            Logger.d("Reminders: Found $reminders reminders")

            val scheduledRemindersIds = mutableListOf<Int>()
            var reminderId = 99
            reminders.forEach { reminder ->
                var reminderTime = startOfSchedulePeriod + reminder.type.time.toDuration()

                do {
                    if (reminderTime < now) {
                        when (reminder.type) {
                            is ReminderType.OneTime -> {
                                reminderTime = reminderTime + 1.days
                            }

                            is ReminderType.Periodic -> {
                                val period = reminder.type.period.toDuration()
                                while (reminderTime < now) {
                                    reminderTime = reminderTime + period
                                }
                            }
                        }
                    }

                    if (reminderTime < endOfSchedulePeriod) {
                        reminderId += 1
                        scheduleReminder(reminderTime + randomReminderWindowDiff(reminder), reminderId, reminder, user, isMetric)
                        scheduledRemindersIds.add(reminderId)

                        if (reminder.type is ReminderType.Periodic) {
                            val period = reminder.type.period.toDuration()
                            reminderTime = reminderTime + period
                        } else {
                            break
                        }
                    }
                } while (reminderTime < endOfSchedulePeriod)
            }

            appPreferences.scheduledRemindersIds = scheduledRemindersIds
        }
    }

    private suspend fun scheduleReminder(
        scheduleAt: Instant,
        id: Int,
        model: ReminderModel,
        user: UserProfileModel,
        isMetric: Boolean,
    ) {
        model.notifications.randomGet().let { notification ->
            notificationScheduler.schedule(
                scheduleAt = scheduleAt,
                notification = Notification(
                    id = id,
                    title = notification.title.bindUserData(user, isMetric),
                    body = notification.description.bindUserData(user, isMetric),
                    link = model.deepLink?.uri,
                )
            )
        }
        Logger.d("Reminders: Scheduled reminder at $scheduleAt")
    }

    override suspend fun clear() {
        val previousRemindersIds = appPreferences.scheduledRemindersIds

        if (previousRemindersIds.isNotEmpty()) {
            Logger.d("Reminders: Canceling previous reminders, ids: $previousRemindersIds")
            notificationScheduler.cancel(previousRemindersIds)
        }
    }

    fun randomReminderWindowDiff(reminder: ReminderModel): Duration {
        val timeWindow = reminder.type.timeWindow?.inWholeMinutes
        if (timeWindow != null) {
            return (0..timeWindow).random().minutes
        }
        return Duration.ZERO
    }
}

private fun Time.toDuration() = this.hour.hours + this.minute.minutes

private fun DatePeriod.toDuration(): Duration {
    return this.days.days + this.minutes.minutes + this.hours.hours
}

private fun String.bindUserData(user: UserProfileModel, isMetric: Boolean): String {
    return user.bindPlaceholderValues(this, isMetric)
}

private fun <T> List<T>.randomGet(): T {
    return this[(this.indices).random()]
}
